<template>
  <div
    v-if="!isSeparatedPriceSelections && version === 'v1'"
    class="nc-text tw-flex tw-w-full tw-flex-row tw-items-center"
  >
    <multiple-input-group popup-width="auto">
      <template #front-item>
        <div class="w-100 d-flex flex-row align-items-center justify-content-center properties-filter-item">
          {{ minPriceLabel }}<span class="px-2">-</span>{{ maxPriceLabel }}
          {{ !filtersModel.min_price && !filtersModel.max_price ? 'Price' : '' }}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10L12 15L17 10H7Z" fill="#AF624E" />
          </svg>
        </div>
      </template>
      <template #group>
        <div class="d-flex flex-row">
          <select v-model="filtersModel.min_price" style="border: unset !important">
            <option
              v-for="(minPriceOption, index) in minPrices"
              :key="`minPriceOption-${index}`"
              :value="minPriceOption.value"
            >
              {{ minPriceOption.display }}
            </option>
          </select>
          <div class="px-2">-</div>
          <select v-model="filtersModel.max_price" style="border: unset !important">
            <option
              v-for="(maxPriceOption, index) in maxPrices"
              :key="`maxPriceOption-${index}`"
              :value="maxPriceOption.value"
            >
              {{ maxPriceOption.display }}
            </option>
          </select>
        </div>
      </template>
    </multiple-input-group>
  </div>
  <div
    v-if="isSeparatedPriceSelections && version === 'v1'"
    class="flex-row nc-text price-input-height tw-flex tw-grid tw-h-[55px] tw-grid-cols-12 tw-bg-white lg:tw-h-full"
  >
    <div
      class="tw-col-span-6 tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-x-3 tw-border-b-0 tw-border-l-0 tw-border-r tw-border-t-0 tw-border-[#CCCCCC]"
    >
      <select v-model="filtersModel.min_price" class="tw-w-auto">
        <option
          v-for="(minPriceOption, index) in minPrices"
          :key="`minPriceOption-${index}`"
          :value="minPriceOption.value"
        >
          {{ minPriceOption.display }}
        </option>
      </select>
    </div>
    <div class="tw-col-span-6 tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-x-3">
      <select v-model="filtersModel.max_price" class="tw-w-auto">
        <option
          v-for="(maxPriceOption, index) in maxPrices"
          :key="`maxPriceOption-${index}`"
          :value="maxPriceOption.value"
        >
          {{ maxPriceOption.display }}
        </option>
      </select>
    </div>
  </div>

  <div v-if="version === 'v2'" class="tw-h-full tw-w-full">
    <multiple-input-group popup-width="auto">
      <template #front-item>
        <div
          class="price-input tw-flex tw-h-full tw-w-full tw-items-center tw-justify-between tw-border-r tw-px-4 tw-py-4"
          style="border-color: var(--c-border)"
        >
          <div>
            {{ priceLabel }}
          </div>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10L12 15L17 10H7Z" fill="var(--c-icon)" />
          </svg>
        </div>
      </template>
      <template #group>
        <div class="tw-flex tw-h-full tw-w-full tw-gap-4 tw-px-2">
          <select v-model="filtersModel.min_price">
            <option
              v-for="(minPriceOption, index) in minPrices"
              :key="`minPriceOption-${index}`"
              :value="minPriceOption.value"
            >
              {{ minPriceOption.display }}
            </option>
          </select>

          <select v-model="filtersModel.max_price">
            <option
              v-for="(maxPriceOption, index) in maxPrices"
              :key="`maxPriceOption-${index}`"
              :value="maxPriceOption.value"
            >
              {{ maxPriceOption.display }}
            </option>
          </select>
        </div>
      </template>
    </multiple-input-group>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { mapState } from 'pinia';
import MultipleInputGroup from '~/components/common/MultipleInputGroup.vue';
import CriteriaPreferencesConstants from '~/units/properties/constants/criteriaPreferences.constants';
import { PFilterSearchType, type PropertiesFilter } from '~/units/properties/types';
import { useWebsiteStore } from '~/units/website/store';
import NcSelect from '~/components/common/inputs/NcSelect.vue';

export default defineNuxtComponent({
  name: 'LibraryPropertiesSearchBoxPriceInput',
  components: { NcSelect, MultipleInputGroup },

  props: {
    filters: {
      required: true,
      type: Object as PropType<PropertiesFilter>,
    },

    searchType: {
      required: true,
      type: String as PropType<PFilterSearchType>,
    },

    version: {
      required: false,
      type: String,
      default: 'v1',
    },
  },

  computed: {
    ...mapState(useWebsiteStore, ['propertiesConfig']),

    filtersModel: {
      get() {
        return this.filters;
      },
      set(value: PropertiesFilter) {
        this.$emit('update:filters', value);
      },
    },

    minPriceLabel() {
      if (this.filtersModel.min_price) {
        const selectedPrice = this.minPrices.find((price) => price.value === this.filtersModel.min_price);
        if (selectedPrice) {
          return selectedPrice.display;
        }
      }
      return 'Min';
    },

    maxPriceLabel() {
      if (this.filtersModel.max_price) {
        const selectedPrice = this.maxPrices.find((price) => price.value === this.filtersModel.max_price);
        if (selectedPrice) {
          return selectedPrice.display;
        }
      }
      return 'Max';
    },

    minPrices() {
      return CriteriaPreferencesConstants.getPrices(this.searchType, 'min', true);
    },

    maxPrices() {
      return CriteriaPreferencesConstants.getPrices(this.searchType, 'max', true);
    },

    isSeparatedPriceSelections() {
      return this.propertiesConfig.isSeparatedPriceSelections;
    },

    priceLabel() {
      const minPrice = this.minPriceLabel || 'Min';
      const maxPrice = this.maxPriceLabel || 'Max';
      if (!this.filtersModel.min_price && !this.filtersModel.max_price) {
        return 'Price Interval';
      }
      return `${minPrice} - ${maxPrice}`;
    },
  },
});
</script>

<style scoped>
.price-select {
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAQCAYAAAAMJL+VAAAABGdBTUEAALGPC/xhBQAAAQtJREFUOBG1lEEOgjAQRalbGj2OG9caOACn4ALGtfEuHACiazceR1PWOH/CNA3aMiTaBDpt/7zPdBKy7M/DCL9pGkvxxVp7KsvyJftL5rZt1865M+Ucq6pyyF3hNcI7Cuu+728QYn/JQA5yKaempxuZmQngOwEaYx55nu+1lQh8GIatMGi+01NwBcEmhxBqK4nAPZJ78K0KKFAJmR3oPp8+Iwgob0Oa6+TLoeCvRx+mTUYf/FVBGTPRwDkfLxnaSrRwcH0FWhNOmrkWYbE2XEicqgSa1J0LQ+aPCuQgZiLnwewbGuz5MGoAhcIkCQcjaTBjMgtXGURMVHC1wcQEy0J+Zlj8bKAnY1/UzDe2dbAVqfXn6wAAAABJRU5ErkJggg==');
  background-size: 0.7rem;
  background-position: right 0.7rem center;
}
</style>
